/* ==========================================================================
   hgt-cart.js — cart drawer, cart items, product form, quantity input
   Implements the shopping flow for the cloned theme (drawer + AJAX cart).
   ========================================================================== */
(function () {
  'use strict';

  /* ------------------------------- helpers -------------------------------- */

  function getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', selector: '.cart-count-bubble' },
    ];
  }

  function sectionIds() {
    return getSectionsToRender().map((section) => section.id);
  }

  function updateSections(sectionsHTML) {
    getSectionsToRender().forEach((section) => {
      const newSectionHTML = sectionsHTML[section.id];
      if (!newSectionHTML) return;

      const parsed = new DOMParser().parseFromString(newSectionHTML, 'text/html');

      if (section.id === 'cart-icon-bubble') {
        // The bubble lives inside the header link (#cart-icon-bubble), not in its own
        // shopify-section wrapper — swap the rendered section's inner HTML into it.
        const liveBubble = document.getElementById('cart-icon-bubble');
        const rendered = parsed.querySelector('#shopify-section-cart-icon-bubble') || parsed.body;
        if (liveBubble && rendered) liveBubble.innerHTML = rendered.innerHTML;
        return;
      }

      const currentSection = document.getElementById(`shopify-section-${section.id}`);
      if (!currentSection) return;

      const newContent = parsed.querySelector(section.selector);
      const currentContent = currentSection.querySelector(section.selector);

      if (section.id === 'cart-drawer') {
        // preserve drawer open state while swapping contents
        const drawer = currentSection.querySelector('cart-drawer');
        const wasActive = drawer && drawer.classList.contains('active');
        if (newContent && currentContent) {
          currentContent.innerHTML = newContent.innerHTML;
        }
        const newDrawer = parsed.querySelector('cart-drawer');
        if (drawer && newDrawer) {
          drawer.classList.toggle('is-empty', newDrawer.classList.contains('is-empty'));
          if (wasActive) drawer.classList.add('active');
        }
        // refresh item-level custom elements
        if (window.initCartItemEvents) window.initCartItemEvents();
      } else if (newContent && currentContent) {
        currentContent.innerHTML = newContent.innerHTML;
      }
    });
  }

  /* ------------------------------- CartDrawer ------------------------------ */

  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('keyup', (evt) => {
        if (window.onKeyUpEscape(evt)) this.close();
      });
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));

      window.subscribe('cart:updated', () => {
        if (!this.classList.contains('active')) this.open();
      });
    }

    open(triggeredBy) {
      if (triggeredBy) this.setActiveElement(triggeredBy);
      this.classList.add('active');
      document.body.classList.add('overflow-hidden');
      const drawerInner = this.querySelector('.drawer__inner');
      if (drawerInner) {
        setTimeout(() => {
          window.trapFocus(drawerInner);
        });
      }
    }

    close() {
      this.classList.remove('active');
      document.body.classList.remove('overflow-hidden');
      window.removeTrapFocus();
    }

    setActiveElement(element) {
      this.activeElement = element;
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  /* ----------------------------- CartDrawerItems --------------------------- */

  class CartDrawerItems extends HTMLElement {
    initEvents() {
      this.querySelectorAll('quantity-input').forEach((input) => {
        if (!input.dataset.initialized) input.init();
      });
      this.querySelectorAll('cart-remove-button').forEach((button) => {
        if (button.dataset.initialized) return;
        button.dataset.initialized = 'true';
        button.addEventListener('click', (event) => {
          event.preventDefault();
          const line = button.dataset.line;
          this.updateQuantity(line, 0);
        });
      });
    }

    connectedCallback() {
      this.initEvents();
      window.initCartItemEvents = this.initEvents.bind(this);
      if (!this.quantityListenerAttached) {
        this.quantityListenerAttached = true;
        this.addEventListener('quantity-change', (event) => {
          const input = event.target.closest('quantity-input');
          const inputEl = input && input.querySelector('input');
          const line = inputEl ? inputEl.dataset.index : null;
          const quantity = event.detail && event.detail.quantity;
          if (line && quantity != null) this.updateQuantity(line, quantity);
        });
      }
      if (!this.removeListenerAttached) {
        // cart-remove-button clicks are re-bound in initEvents (nodes are replaced on refresh)
      }
    }

    updateQuantity(line, quantity, name) {
      this.setLoadingState();
      const body = JSON.stringify({
        line,
        quantity,
        sections: sectionIds(),
        sections_url: window.location.pathname,
      });

      fetch(`${window.routes.cart_change_url}`, { ...window.fetchConfig, ...{ body } })
        .then((response) => response.text())
        .then((state) => {
          const parsedState = JSON.parse(state);
          if (parsedState.errors) {
            this.updateErrors(parsedState.description, parsedState.message);
            return;
          }
          this.removeLoadingState();
          if (parsedState.sections) updateSections(parsedState.sections);
          window.publish('cart:updated');
        })
        .catch(() => {
          this.removeLoadingState();
        });
    }

    setLoadingState() {
      const overlay = this.querySelector('.loading-overlay');
      if (overlay) overlay.classList.remove('hidden');
      this.classList.add('cart-drawer--loading');
    }

    removeLoadingState() {
      const overlay = this.querySelector('.loading-overlay');
      if (overlay) overlay.classList.add('hidden');
      this.classList.remove('cart-drawer--loading');
    }

    updateErrors(description, message) {
      const errors = document.getElementById('CartDrawer-CartErrors');
      if (errors) errors.textContent = message || description;
    }
  }
  customElements.define('cart-drawer-items', CartDrawerItems);

  /* ------------------------------ QuantityInput ---------------------------- */

  class QuantityInput extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input');
      this.changeEvent = new Event('change', { bubbles: true });
    }

    init() {
      if (this.input) {
        this.input.addEventListener('change', this.onInputChange.bind(this));
      }
      this.querySelectorAll('button').forEach((button) =>
        button.addEventListener('click', this.onButtonClick.bind(this))
      );
      this.dataset.initialized = 'true';
    }

    connectedCallback() {
      if (this.input) this.init();
    }

    onButtonClick(event) {
      event.preventDefault();
      const previousValue = this.input.value;
      if (event.target.name === 'plus') {
        this.input.stepUp();
      } else {
        this.input.stepDown();
      }
      if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
    }

    onInputChange(event) {
      const previousValue = event.target.defaultValue;
      const quantity = parseInt(event.target.value, 10);
      if (quantity > 0 && previousValue != event.target.value) {
        this.dispatchEvent(new CustomEvent('quantity-change', { bubbles: true, detail: { quantity } }));
      }
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ------------------------------ ProductForm ------------------------------ */

  class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cart = document.querySelector('cart-drawer');
      this.submitButton = this.querySelector('[type="submit"]');
      this.isCartUpsell = this.dataset.isCartUpsell === 'true';
      if (this.isCartUpsell) this.cartDrawer = document.querySelector('cart-drawer');
    }

    onSubmitHandler(evt) {
      evt.preventDefault();
      if (this.submitButton) {
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        this.submitButton.setAttribute('aria-disabled', true);
      }
      this.handleSubmit(evt);
      return false;
    }

    handleSubmit(evt) {
      this.errors = this.errors || document.getElementById(this.getSectionId() + '-error-message');
      const config = {
        ...window.fetchConfig,
        body: new FormData(this.form),
      };
      config.body.append('sections', sectionIds());
      config.body.append('sections_url', window.location.pathname);

      fetch(`${window.routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.handleErrorMessage(response.description);
            return;
          }
          if (response.sections) updateSections(response.sections);
          window.publish('cart:updated', { source: this.isCartUpsell ? 'upsell' : 'product' });
          if (!this.isCartUpsell && this.cart) this.cart.open();
          this.querySelectorAll('.loading-overlay').forEach((o) => o.classList.remove('hidden'));
          setTimeout(() => {
            this.querySelectorAll('.loading-overlay').forEach((o) => o.classList.add('hidden'));
            if (this.submitButton) this.submitButton.removeAttribute('aria-disabled');
          }, 600);
        })
        .catch((error) => {
          console.error(error);
          if (this.submitButton) this.submitButton.removeAttribute('aria-disabled');
        });
    }

    getSectionId() {
      const sectionId = this.dataset.section || (this.dataset.sectionId ? `${this.dataset.sectionId}-main` : '');
      return sectionId;
    }

    handleErrorMessage(errorMessage) {
      const wrapper = this.querySelector('.product-form__error-message-wrapper');
      if (!wrapper) return;
      wrapper.hidden = !errorMessage;
      const message = wrapper.querySelector('.product-form__error-message');
      if (message) message.textContent = errorMessage;
      if (this.submitButton) this.submitButton.removeAttribute('aria-disabled');
      setTimeout(() => {
        wrapper.hidden = true;
      }, 5000);
    }
  }
  customElements.define('product-form', ProductForm);

  /* --------------------------- CartDrawerUpsell ---------------------------- */

  class CartDrawerUpsell extends HTMLElement {
    connectedCallback() {
      this.toggleButton = this.querySelector('.upsell-toggle-btn');
      this.productForm = this.querySelector('product-form');
      if (!this.toggleButton) return;
      this.toggleButton.addEventListener('click', this.onToggle.bind(this));
    }

    onToggle(event) {
      event.preventDefault();
      const variantId = this.dataset.id;
      const selected = this.dataset.selected === 'true';

      if (!selected) {
        // add the upsell product
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');
        formData.append('sections', sectionIds().join(','));
        formData.append('sections_url', window.location.pathname);
        fetch(`${window.routes.cart_add_url}`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
        })
          .then((r) => r.json())
          .then((response) => {
            if (response.sections) updateSections(response.sections);
            this.dataset.selected = 'true';
            this.classList.add('upsell--selected');
            window.publish('cart:updated');
          })
          .catch((e) => console.error(e));
      } else {
        // remove the upsell product: find its line and set quantity 0
        fetch(`${window.routes.cart_url}.js`)
          .then((r) => r.json())
          .then((cart) => {
            const item = cart.items.find((i) => String(i.id) === String(variantId));
            if (!item) {
              this.dataset.selected = 'false';
              this.classList.remove('upsell--selected');
              return;
            }
            const body = JSON.stringify({
              line: item.key,
              quantity: 0,
              sections: sectionIds(),
              sections_url: window.location.pathname,
            });
            fetch(`${window.routes.cart_change_url}`, { ...window.fetchConfig, body })
              .then((r) => r.json())
              .then((response) => {
                if (response.sections) updateSections(response.sections);
                this.dataset.selected = 'false';
                this.classList.remove('upsell--selected');
                window.publish('cart:updated');
              });
          })
          .catch((e) => console.error(e));
      }
    }
  }
  customElements.define('cart-drawer-upsell', CartDrawerUpsell);

  /* -------------------------------- StickyAtc ------------------------------ */

  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.mainForm = document.querySelector('product-form[data-main="true"]');
      const button = this.querySelector('.sticky-atc__add_button');
      if (button && this.mainForm) {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          this.mainForm.handleSubmit(event);
        });
      }
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* -------------------------- Product variant picker ----------------------- */

  class VariantSelects extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', this.onVariantChange.bind(this));
      this.updateOptions();
    }

    onVariantChange() {
      this.updateOptions();
      this.updateMasterId();
      this.toggleAddButton(true, '', false);
      this.updateVariantStatuses();
    }

    updateOptions() {
      this.options = Array.from(this.querySelectorAll('input[type="radio"]:checked'));
    }

    updateMasterId() {
      const masterId = this.getVariantData().find((variant) => {
        return !variant.options
          .map((option, index) => {
            return this.options[index].value === option;
          })
          .includes(false);
      });
      if (!masterId) return;
      this.currentVariant = masterId;
      const form = this.closest('product-form');
      if (!form) return;
      const idInput = form.querySelector('form [name="id"]');
      if (idInput) idInput.value = masterId.id;
      form.querySelectorAll('[data-variant-id]').forEach((el) => {
        el.dataset.variantId = masterId.id;
      });

      // update URL + price displays
      if (!masterId.available) {
        this.toggleAddButton(true, window.variantStrings.soldOut, true);
      } else {
        this.toggleAddButton(false, window.variantStrings.addToCart, false);
      }
      this.updatePriceDisplay(masterId);
      this.updateMedia(masterId);
      if (this.dataset.updateUrl === 'true') {
        window.history.replaceState({}, '', `${this.dataset.url}?variant=${masterId.id}`);
      }
    }

    updatePriceDisplay(variant) {
      const priceEl = document.querySelector(`[data-price-container]`);
      if (priceEl && variant) {
        const price = window.formatMoney(variant.price);
        const priceContainer = priceEl.querySelector('.price-item--regular') || priceEl;
        priceContainer.textContent = price;
      }
    }

    updateMedia(variant) {
      if (!variant || !variant.featured_media) return;
      const mediaId = variant.featured_media.id;
      const mediaEl = document.querySelector(`[data-media-id="${mediaId}"]`);
      if (mediaEl && mediaEl.closest('media-gallery')) {
        mediaEl.closest('media-gallery').setActiveMedia(mediaId);
      }
    }

    toggleAddButton(disable, text, isSoldOut) {
      const form = this.closest('product-form');
      if (!form) return;
      const button = form.querySelector('[type="submit"]');
      if (!button) return;
      if (disable) {
        button.setAttribute('disabled', 'disabled');
        button.setAttribute('aria-disabled', true);
      } else {
        button.removeAttribute('disabled');
        button.removeAttribute('aria-disabled');
      }
      const label = button.querySelector('.button__label') || button;
      if (text) label.textContent = text;
    }

    updateVariantStatuses() {
      const variants = this.getVariantData();
      const selectedOptionValues = this.options.map((o) => o.value);
      this.querySelectorAll('input[type="radio"]').forEach((input) => {
        const optionPosition = parseInt(input.dataset.optionPosition, 10);
        let variantAvailable = false;
        variants.forEach((variant) => {
          const newOptionValues = [...selectedOptionValues];
          newOptionValues[optionPosition - 1] = input.value;
          const matches = variant.options.every((option, index) => newOptionValues[index] === option);
          if (matches && variant.available) variantAvailable = true;
        });
        input.disabled = !variantAvailable;
        const label = input.closest('label');
        if (label) label.classList.toggle('disabled', !variantAvailable);
      });
    }

    getVariantData() {
      this.variantData = this.variantData || JSON.parse(this.querySelector('[data-variant-json]')?.textContent || '[]');
      return this.variantData;
    }
  }
  customElements.define('variant-selects', VariantSelects);

  /* ------------------------------- MediaGallery ---------------------------- */

  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.elements = {
        mediaItems: this.querySelectorAll('[data-media-id]'),
      };
      this.addEventListener('click', (event) => {
        const target = event.target.closest('[data-media-id]');
        if (target && target.closest('.product__media-list')) {
          event.preventDefault();
          this.setActiveMedia(target.dataset.mediaId, true);
        }
      });
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.querySelector(`[data-media-id="${mediaId}"]`);
      if (!activeMedia) return;
      activeMedia.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      this.dispatchEvent(
        new CustomEvent('media:changed', { bubbles: true, detail: { mediaId } })
      );
    }
  }
  customElements.define('media-gallery', MediaGallery);
})();
